import React, { useState, useEffect } from 'react';
import { getToken } from '../../../../../../services/api';
import './CampaignHistory.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CampaignHistory = ({ state }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filter, setFilter] = useState('all'); // all, completed, failed, paused
  
  useEffect(() => {
    fetchCampaigns();
  }, [state]);
  
  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/email/${state.toLowerCase()}/campaigns`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch campaigns');
      }
      
      const data = await response.json();
      setCampaigns(data.campaigns);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredCampaigns = campaigns.filter(campaign => {
    if (filter === 'all') return true;
    return campaign.status === filter;
  });
  
  const formatDate = (date) => {
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getDuration = (start, end) => {
    if (!end) return '-';
    const diff = new Date(end) - new Date(start);
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✓';
      case 'failed': return '✗';
      case 'paused': return '⏸';
      case 'sending': return '📤';
      default: return '•';
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#27ae60';
      case 'failed': return '#e74c3c';
      case 'paused': return '#f39c12';
      case 'sending': return '#3498db';
      default: return '#95a5a6';
    }
  };
  
  const downloadReport = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/email/${state.toLowerCase()}/campaign/${campaignId}/report`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `campaign-report-${campaignId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading report:', err);
    }
  };
  
  if (loading) {
    return (
      <div className="campaign-history-loading">
        <div className="spinner"></div>
        <p>Carregando histórico de campanhas...</p>
      </div>
    );
  }
  
  return (
    <div className="campaign-history-container">
      <div className="history-header">
        <h3>Histórico de Campanhas</h3>
        <div className="history-filters">
          <button 
            className={`filter-button ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Todas ({campaigns.length})
          </button>
          <button 
            className={`filter-button ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Concluídas ({campaigns.filter(c => c.status === 'completed').length})
          </button>
          <button 
            className={`filter-button ${filter === 'failed' ? 'active' : ''}`}
            onClick={() => setFilter('failed')}
          >
            Falhadas ({campaigns.filter(c => c.status === 'failed').length})
          </button>
          <button 
            className={`filter-button ${filter === 'paused' ? 'active' : ''}`}
            onClick={() => setFilter('paused')}
          >
            Pausadas ({campaigns.filter(c => c.status === 'paused').length})
          </button>
        </div>
      </div>
      
      {filteredCampaigns.length === 0 ? (
        <div className="no-campaigns">
          <p>Nenhuma campanha encontrada</p>
        </div>
      ) : (
        <div className="campaigns-table">
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Nome da Campanha</th>
                <th>Template</th>
                <th>Enviados / Total</th>
                <th>Taxa de Sucesso</th>
                <th>Criado em</th>
                <th>Duração</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.map(campaign => {
                const successRate = campaign.stats?.total > 0
                  ? Math.round((campaign.stats.sent / campaign.stats.total) * 100)
                  : 0;
                  
                return (
                  <tr key={campaign._id}>
                    <td>
                      <span 
                        className="status-icon"
                        style={{ color: getStatusColor(campaign.status) }}
                      >
                        {getStatusIcon(campaign.status)}
                      </span>
                    </td>
                    <td className="campaign-name">{campaign.name}</td>
                    <td>{campaign.templateName || campaign.templateId}</td>
                    <td>
                      <span className="count-badge">
                        {campaign.stats?.sent || 0} / {campaign.stats?.total || 0}
                      </span>
                    </td>
                    <td>
                      <div className="success-rate">
                        <div className="rate-bar">
                          <div 
                            className="rate-fill"
                            style={{ 
                              width: `${successRate}%`,
                              backgroundColor: successRate > 80 ? '#27ae60' : '#f39c12'
                            }}
                          />
                        </div>
                        <span className="rate-text">{successRate}%</span>
                      </div>
                    </td>
                    <td>{formatDate(campaign.createdAt)}</td>
                    <td>{campaign.duration || getDuration(campaign.createdAt, campaign.completedAt || campaign.pausedAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="view-button"
                          onClick={() => setSelectedCampaign(campaign)}
                          title="Ver detalhes"
                        >
                          👁️
                        </button>
                        <button 
                          className="download-button"
                          onClick={() => downloadReport(campaign._id)}
                          title="Baixar relatório"
                        >
                          📥
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {selectedCampaign && (
        <div className="campaign-modal-overlay" onClick={() => setSelectedCampaign(null)}>
          <div className="campaign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedCampaign.name}</h3>
              <button className="close-button" onClick={() => setSelectedCampaign(null)}>
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="detail-section">
                <h4>Informações Gerais</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Status:</span>
                    <span className="value" style={{ color: getStatusColor(selectedCampaign.status) }}>
                      {selectedCampaign.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Template:</span>
                    <span className="value">{selectedCampaign.templateName || selectedCampaign.templateId}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Assunto:</span>
                    <span className="value">{selectedCampaign.subject}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Criado por:</span>
                    <span className="value">{selectedCampaign.createdBy?.name || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              <div className="detail-section">
                <h4>Estatísticas de Envio</h4>
                <div className="stats-grid">
                  <div className="stat-card">
                    <span className="stat-value">{selectedCampaign.stats?.total || 0}</span>
                    <span className="stat-label">Total de Destinatários</span>
                  </div>
                  <div className="stat-card success">
                    <span className="stat-value">{selectedCampaign.stats?.sent || 0}</span>
                    <span className="stat-label">Enviados com Sucesso</span>
                  </div>
                  <div className="stat-card error">
                    <span className="stat-value">{selectedCampaign.stats?.failed || 0}</span>
                    <span className="stat-label">Falhas no Envio</span>
                  </div>
                </div>
              </div>
              
              {selectedCampaign.batchConfig && (
                <div className="detail-section">
                  <h4>Configuração de Lotes</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Tamanho do Lote:</span>
                      <span className="value">{selectedCampaign.batchConfig.batchSize} emails</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Intervalo entre Lotes:</span>
                      <span className="value">{selectedCampaign.batchConfig.delaySeconds} segundos</span>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedCampaign.error && (
                <div className="detail-section error">
                  <h4>Erro</h4>
                  <p>{selectedCampaign.error}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignHistory;